import React, { useRef, useState } from 'react';
import { Button, ButtonGroup, ClickAwayListener, Grow, Paper, Popper, MenuItem, MenuList } from '@mui/material';
import { ArrowDropDown } from '@mui/icons-material';
import styled from 'styled-components';
import MeasurementType from 'types/MeasurementType';
import UnitType from 'types/UnitType';

interface SplitButtonProps {
  style?: React.CSSProperties;
  options: { value: string | null; label: string }[];
  optionSelected?: MeasurementType | UnitType | null;
  onClick?: (key: string | null) => void;
  onOptionChange?: (key: string | null) => void;
}

const SplitButtonContainer = styled.div``;

const StyledButton = styled(Button)`
  white-space: nowrap;
`;

const StyledPopper = styled(Popper)`
  z-index: 1;
`;

const SplitButton: React.FC<SplitButtonProps> = ({ options, optionSelected, onClick, onOptionChange, style }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const selectedOptionIndex = optionSelected ? options.map((option) => option.value).indexOf(optionSelected) : 0;

  const [selectedIndex, setSelectedIndex] = useState(selectedOptionIndex);

  const handleOnClick = () => {
    if (onClick) {
      const { value } = options[selectedIndex];
      onClick(value);
    }
  };

  const handleOnToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleOnMenuItemClick = (_: React.MouseEvent<HTMLLIElement, MouseEvent>, index: number) => {
    if (onOptionChange) {
      const { value } = options[index];
      onOptionChange(value);
    }
    setSelectedIndex(index);
    setOpen(false);
  };

  const handleOnClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  const buttonDisabled = options.some(
    (option) =>
      Object.values(MeasurementType).includes(option.value as MeasurementType) ||
      Object.values(UnitType).includes(option.value as UnitType),
  );

  return (
    <SplitButtonContainer style={style}>
      <ButtonGroup variant="contained" color="primary" ref={anchorRef} aria-label="split button">
        <StyledButton onClick={handleOnClick} disabled={buttonDisabled}>
          {options[selectedIndex].label}
        </StyledButton>
        <Button
          color="primary"
          size="small"
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-label="select merge strategy"
          aria-haspopup="menu"
          onClick={handleOnToggle}
        >
          <ArrowDropDown />
        </Button>
      </ButtonGroup>
      <StyledPopper open={open} anchorEl={anchorRef.current} role={undefined} transition disablePortal>
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener mouseEvent="onMouseDown" touchEvent="onTouchStart" onClickAway={handleOnClose}>
                <MenuList id="split-button-menu">
                  {options.map(({ value, label }, index) => (
                    <MenuItem
                      key={value}
                      selected={index === selectedIndex}
                      onClick={(event) => handleOnMenuItemClick(event, index)}
                    >
                      {label}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </StyledPopper>
    </SplitButtonContainer>
  );
};

export default SplitButton;
